import { useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { sendContactMessage } from "~/server/contact";

const topics = [
  "General question",
  "Shared hosting",
  "Application hosting",
  "Hosted databases",
  "Larger workload",
];

export function ContactForm() {
  const [status, setStatus] = useState<"idle" | "sent" | "error">("idle");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const sendMessage = useServerFn(sendContactMessage);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = formRef.current;
    if (!formElement) return;
    setStatus("idle");
    setError("");
    setSending(true);
    const form = new FormData(formElement);
    try {
      await sendMessage({
        data: {
          name: String(form.get("name") ?? ""),
          email: String(form.get("email") ?? ""),
          company: String(form.get("company") ?? ""),
          topic: String(form.get("topic") ?? ""),
          message: String(form.get("message") ?? ""),
        },
      });
      formElement.reset();
      setStatus("sent");
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "We could not send your message. Please try again.",
      );
      setStatus("error");
    } finally {
      setSending(false);
    }
  }

  return (
    <form
      ref={formRef}
      onSubmit={submit}
      className="rounded-[2rem] border border-ink/10 bg-white p-6 shadow-[8px_8px_0_#dce9e2] sm:p-8"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Name" name="name" autoComplete="name" required />
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
        />
      </div>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <Field
          label="Company or project"
          name="company"
          autoComplete="organization"
        />
        <label className="grid gap-2 text-sm font-bold">
          What can we help with?
          <select
            name="topic"
            defaultValue="General question"
            className="rounded-xl border border-ink/15 bg-cream px-4 py-3 font-medium outline-none focus:border-moss"
          >
            {topics.map((topic) => (
              <option key={topic}>{topic}</option>
            ))}
          </select>
        </label>
      </div>
      <label className="mt-5 grid gap-2 text-sm font-bold">
        Tell us a little more
        <textarea
          name="message"
          required
          rows={6}
          className="resize-y rounded-xl border border-ink/15 bg-cream px-4 py-3 font-medium outline-none focus:border-moss"
          placeholder="What are you building, or what do you need help with?"
        />
      </label>
      <div className="mt-6 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={sending}
          className="button-primary rounded-full px-6 py-3.5 text-sm font-bold disabled:cursor-wait disabled:opacity-60"
        >
          {sending ? "Sending…" : "Send message"}
        </button>
        <p className="text-sm text-ink/55">
          We will reply to the email address you provide.
        </p>
      </div>
      {status === "sent" && (
        <p className="mt-5 rounded-xl bg-cloud px-4 py-3 text-sm font-bold text-moss">
          Thanks, your message is on its way.
        </p>
      )}
      {status === "error" && (
        <p className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-bold text-red-800">
          {error}
        </p>
      )}
    </form>
  );
}

function Field({
  label,
  name,
  type = "text",
  autoComplete,
  required = false,
}: {
  label: string;
  name: string;
  type?: string;
  autoComplete?: string;
  required?: boolean;
}) {
  return (
    <label className="grid gap-2 text-sm font-bold">
      {label}
      <input
        name={name}
        type={type}
        autoComplete={autoComplete}
        required={required}
        className="rounded-xl border border-ink/15 bg-cream px-4 py-3 font-medium outline-none focus:border-moss"
      />
    </label>
  );
}
